import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, fmtEUR } from "@/lib/api";
import { PRICING, DISCOUNTS } from "@/data/pricing";
import { TURNSTILE_SITE_KEY } from "@/components/site/Turnstile";

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

export function computeUnit(matrix, breakpoints, screen, qty, minQ, maxQ) {
  const tiers = matrix?.[screen];
  if (!tiers) return 0;
  const bps = breakpoints;
  const q = Math.max(minQ, Math.min(maxQ, qty));
  if (q <= bps[0]) return tiers[String(bps[0])];
  if (q >= bps[bps.length - 1]) return tiers[String(bps[bps.length - 1])];
  for (let i = 0; i < bps.length - 1; i += 1) {
    const lo = bps[i];
    const hi = bps[i + 1];
    if (q >= lo && q <= hi) {
      const t = (q - lo) / (hi - lo);
      return round2(tiers[String(lo)] + (tiers[String(hi)] - tiers[String(lo)]) * t);
    }
  }
  return tiers[String(bps[bps.length - 1])];
}

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  comments: "",
  consent: false,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useCalculator() {
  const [pricing, setPricing] = useState(null);
  const [screen, setScreen] = useState("5.0");
  const [qty, setQty] = useState(200);
  const [product, setProduct] = useState("Video Brochure");
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [token, setToken] = useState("");
  const [website, setWebsite] = useState(""); // honeypot

  useEffect(() => {
    // No backend — pricing is bundled with the app.
    setPricing(PRICING);
  }, []);

  const quote = useMemo(() => {
    if (!pricing) return null;
    const unit = computeUnit(pricing.matrix, pricing.breakpoints, screen, qty, pricing.min_qty, pricing.max_qty);
    const subtotal = round2(unit * qty);
    const percent = discount?.percent || 0;
    const discountAmount = round2((subtotal * percent) / 100);
    const total = round2(subtotal - discountAmount);
    // Effective per-unit price after the discount, so the calculator can show
    // the reduced price/τεμάχιο (not just the reduced total).
    const unitNet = round2(unit * (1 - percent / 100));
    return { unit, unitNet, subtotal, percent, discountAmount, total };
  }, [pricing, screen, qty, discount]);

  const screenMeta = useMemo(
    () => pricing?.screens?.find((s) => s.id === screen) || null,
    [pricing, screen]
  );

  // Pricing is only available for the Video Brochure. Any other product type
  // shows a "request a quote" state instead of a calculated price.
  const priceActive = product === "Video Brochure";

  const setQuantity = useCallback(
    (value) => {
      const min = pricing?.min_qty ?? 100;
      const max = pricing?.max_qty ?? 5000;
      const v = Number.isNaN(value) ? min : value;
      setQty(Math.max(min, Math.min(max, v)));
    },
    [pricing]
  );

  const applyCode = useCallback(async () => {
    if (!code.trim()) return;
    setCheckingCode(true);
    try {
      // Discount codes are validated client-side (no backend).
      const local = DISCOUNTS[code.trim().toUpperCase()];
      if (local) {
        setDiscount(local);
        toast.success(`Εφαρμόστηκε έκπτωση ${local.percent}%`);
      } else {
        setDiscount(null);
        toast.error("Μη έγκυρος κωδικός έκπτωσης");
      }
    } finally {
      setCheckingCode(false);
    }
  }, [code]);

  const setField = useCallback(
    (key) => (e) => {
      const value = e && e.target ? e.target.value : e;
      setForm((f) => ({ ...f, [key]: value }));
    },
    []
  );

  const setConsent = useCallback((value) => {
    setForm((f) => ({ ...f, consent: !!value }));
  }, []);

  const validate = useCallback(() => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Υποχρεωτικό πεδίο";
    if (!form.last_name.trim()) e.last_name = "Υποχρεωτικό πεδίο";
    if (!EMAIL_RE.test(form.email)) e.email = "Μη έγκυρο email";
    if (!form.phone.trim()) e.phone = "Υποχρεωτικό πεδίο";
    if (!form.consent) e.consent = "Απαιτείται η συγκατάθεση";
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [form]);

  const submitOrder = useCallback(
    async (ev) => {
      ev.preventDefault();
      if (!validate()) {
        toast.error("Συμπληρώστε σωστά τα στοιχεία σας");
        return;
      }
      if (TURNSTILE_SITE_KEY && !token) {
        toast.error("Ολοκλήρωσε την επαλήθευση ασφαλείας.");
        return;
      }
      setSubmitting(true);
      const screenLabel = screenMeta?.name || `${screen}" Οθόνη`;
      const effectiveUnit = quote && quote.percent > 0 ? quote.unitNet : quote?.unit;
      const unitDisplay = priceActive && quote ? fmtEUR(effectiveUnit) : "Ζητήστε προσφορά";
      const totalDisplay = priceActive && quote ? fmtEUR(quote.total) : "Ζητήστε προσφορά";
      try {
        await api.post("/orders", {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
          comments: form.comments,
          product_type: product,
          screen: screenLabel,
          quantity: qty,
          unit_price: unitDisplay,
          total_price: totalDisplay,
          discount_code: discount?.code || "",
          website,
          turnstileToken: token,
        });
        setDone(true);
        toast.success("Το αίτημα παραγγελίας στάλθηκε!");
        setForm(EMPTY_FORM);
        setToken("");
      } catch (err) {
        toast.error("Κάτι πήγε στραβά. Δοκίμασε ξανά.");
      } finally {
        setSubmitting(false);
      }
    },
    [validate, screen, qty, product, discount, form, quote, screenMeta, priceActive, token, website]
  );

  const resetDone = useCallback(() => setDone(false), []);

  return {
    pricing, screen, setScreen, qty, setQuantity,
    product, setProduct, priceActive,
    code, setCode, discount, checkingCode, applyCode,
    form, setField, setConsent, errors,
    submitting, done, resetDone, submitOrder,
    quote, screenMeta,
    setToken, website, setWebsite,
  };
}
